import { z } from 'zod';
export const analyticsPresetSchema = z.enum([
    '7d',
    '30d',
    '3m',
    '6m',
    '12m',
    'all',
]);
export const analyticsDateQuerySchema = z.object({
    preset: analyticsPresetSchema.optional(),
    from: z.string().optional(),
    to: z.string().optional(),
});
export const analyticsSeriesPointSchema = z.object({
    day: z.string(),
    value: z.number(),
});
export const analyticsMetricCardSchema = z.object({
    key: z.string(),
    value: z.number(),
    previousValue: z.number(),
    deltaPct: z.number().nullable(),
    series: analyticsSeriesPointSchema.array().optional(),
});
export const analyticsHeatmapCellSchema = z.object({
    dow: z.number().int().min(0).max(6),
    hour: z.number().int().min(0).max(23),
    value: z.number(),
});
/** One cell per calendar day for period heatmaps. */
export const analyticsDayHeatmapCellSchema = z.object({
    day: z.string(),
    value: z.number(),
});
export const analyticsGroupRowSchema = z.object({
    groupId: z.string(),
    handle: z.string().optional(),
    name: z.string().optional(),
    posts: z.number(),
    likes: z.number(),
    comments: z.number(),
    uniqueViewers: z.number(),
    activity: z.number(),
    visibility: z.enum(['public', 'private']),
    members: z.number(),
    activeMembers: z.number(),
    /** (likes + comments) / unique viewers, as a percentage. */
    engagementRate: z.number(),
    /** New members who joined the group in the selected range. */
    growth: z.number(),
    /** Average unique viewers per day in the selected range. */
    dailyVisits: z.number(),
});
export const analyticsGroupGrowthSeriesSchema = z.object({
    key: z.string(),
    groupId: z.string(),
    name: z.string(),
});
export const analyticsGroupGrowthPointSchema = z.object({
    day: z.string(),
    label: z.string(),
    values: z.record(z.string(), z.number()),
});
export const analyticsPostRowSchema = z.object({
    postId: z.string(),
    snippet: z.string().optional(),
    authorHandle: z.string().optional(),
    authorDisplayName: z.string().optional(),
    postType: z.string().optional(),
    likes: z.number().optional(),
    comments: z.number().optional(),
    reposts: z.number().optional(),
    uniqueViewers: z.number(),
    viewEvents: z.number().optional(),
});
export const analyticsMemberRowSchema = z.object({
    profileId: z.string(),
    handle: z.string(),
    displayName: z.string().optional(),
    role: z.string().optional(),
    joinedAt: z.string(),
    contributions: z.number(),
});
export const analyticsPostTypeKeySchema = z.enum([
    'jam',
    'article',
    'note',
    'poll',
    'event',
]);
export const analyticsPostTypeCountSchema = z.object({
    type: analyticsPostTypeKeySchema,
    count: z.number(),
});
export const analyticsStackedPointSchema = z.object({
    day: z.string(),
    label: z.string(),
    jam: z.number(),
    article: z.number(),
    note: z.number(),
    poll: z.number(),
    event: z.number(),
});
export const analyticsBucketedPointSchema = z.object({
    day: z.string(),
    label: z.string(),
    value: z.number(),
});
export const analyticsSignupRowSchema = z.object({
    profileId: z.string(),
    handle: z.string(),
    displayName: z.string().optional(),
    joinedAt: z.string(),
    channel: z.string(),
});
export const analyticsChannelPointSchema = z.object({
    day: z.string(),
    channel: z.string(),
    count: z.number(),
});
export const analyticsRangeMetaSchema = z.object({
    from: z.string(),
    to: z.string(),
    previousFrom: z.string(),
    previousTo: z.string(),
    preset: analyticsPresetSchema.optional(),
    compiledAt: z.string().nullable(),
});
export const analyticsOverviewSchema = z.object({
    range: analyticsRangeMetaSchema,
    metrics: z.object({
        totalPosts: analyticsMetricCardSchema,
        /** Cumulative posts created on or before the range end. */
        allTimePosts: analyticsMetricCardSchema,
        totalMembers: analyticsMetricCardSchema,
        /** Cumulative local members with a verified email, on or before the range end. */
        verifiedMembers: analyticsMetricCardSchema,
        /** Cumulative local members with an unverified email, on or before the range end. */
        unverifiedMembers: analyticsMetricCardSchema,
        activeMembers: analyticsMetricCardSchema,
        totalGroups: analyticsMetricCardSchema,
    }),
    postsOverTime: analyticsStackedPointSchema.array(),
    postTypes: analyticsPostTypeCountSchema.array(),
    activeUsersSeries: analyticsBucketedPointSchema.array(),
    topMembers: analyticsMemberRowSchema.array(),
    topPosts: analyticsPostRowSchema.array(),
    /** @deprecated kept for report/CSV compatibility */
    activitySeries: analyticsSeriesPointSchema.array(),
    engagementRateSeries: analyticsSeriesPointSchema.array(),
    topGroups: analyticsGroupRowSchema.array(),
    /** Top groups by new-member growth, for the multi-line growth chart. */
    groupGrowthSeries: analyticsGroupGrowthSeriesSchema.array(),
    groupGrowthOverTime: analyticsGroupGrowthPointSchema.array(),
});
export const analyticsGrowthSchema = z.object({
    range: analyticsRangeMetaSchema,
    metrics: z.object({
        newSignups: analyticsMetricCardSchema,
        dau: analyticsMetricCardSchema,
        mau: analyticsMetricCardSchema,
        dauMau: analyticsMetricCardSchema,
    }),
    /** New-member counts for every day in the selected range. */
    signupsByDay: analyticsDayHeatmapCellSchema.array(),
    recentSignups: analyticsSignupRowSchema.array(),
});
export const analyticsEngagementOverTimePointSchema = z.object({
    day: z.string(),
    label: z.string(),
    likes: z.number(),
    comments: z.number(),
    reposts: z.number(),
    bookmarks: z.number(),
    dms: z.number(),
});
export const analyticsEngagementSchema = z.object({
    range: analyticsRangeMetaSchema,
    metrics: z.object({
        likes: analyticsMetricCardSchema,
        comments: analyticsMetricCardSchema,
        reposts: analyticsMetricCardSchema,
        bookmarks: analyticsMetricCardSchema,
        dms: analyticsMetricCardSchema,
        uniqueViewers: analyticsMetricCardSchema,
        impressions: analyticsMetricCardSchema,
    }),
    engagementOverTime: analyticsEngagementOverTimePointSchema.array(),
    impressionsByPeriod: analyticsBucketedPointSchema.array(),
    postsByGroup: analyticsGroupRowSchema.array(),
});
export const analyticsRetentionSchema = z.object({
    range: analyticsRangeMetaSchema,
    metrics: z.object({
        day1: analyticsMetricCardSchema,
        day7: analyticsMetricCardSchema,
        day30: analyticsMetricCardSchema,
    }),
    cohorts: z
        .object({
        cohortWeek: z.string(),
        cohortSize: z.number(),
        rates: z.record(z.string(), z.number()),
    })
        .array(),
    retentionByGroup: analyticsGroupRowSchema.array(),
});
export const analyticsReportSettingsSchema = z.object({
    enabled: z.boolean(),
    recipients: z.string().email().array(),
});
export const analyticsBackfillInputSchema = z.object({
    from: z.string().optional(),
    to: z.string().optional(),
});
export const analyticsBackfillResponseSchema = z.object({
    jobId: z.string().optional(),
    from: z.string(),
    to: z.string(),
});
export const analyticsClickKindSchema = z.enum(['page', 'link']);
export const analyticsClickEventSchema = z.object({
    kind: analyticsClickKindSchema,
    target: z.string().min(1).max(2048),
});
export const analyticsClicksIngestSchema = z.object({
    events: analyticsClickEventSchema.array().min(1).max(20),
});
export const analyticsClickRowSchema = z.object({
    target: z.string(),
    clicks: z.number(),
});
export const analyticsClicksSchema = z.object({
    range: analyticsRangeMetaSchema,
    pages: analyticsClickRowSchema.array(),
    links: analyticsClickRowSchema.array(),
});
